import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Plus, Edit2, Trash2, X, Check, Loader2,
  Package, Filter, ChevronDown, BarChart3, Upload, Download,
  CheckSquare, Square, Image as ImageIcon, ScanBarcode, Printer
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import ShopLayout from '@/components/shop/ShopLayout';
import CameraBarcodeScanner from '@/components/shop/CameraBarcodeScanner';
import BarcodePrintModal from '@/components/shop/BarcodePrintModal';
import BulkBarcodePrintModal from '@/components/shop/BulkBarcodePrintModal';
import BrowseCatalogModal from '@/components/shop/BrowseCatalogModal';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TAX_RATES = [
  { value: 4, label: 'Superreducido (4%)' },
  { value: 10, label: 'Reducido (10%)' },
  { value: 21, label: 'General (21%)' },
];

const UNITS = ['unidad', 'kg', 'litro'];

const emptyProduct = {
  name: '', description: '', price: '', category: '',
  barcode: '', stock_count: '', low_stock_threshold: 5,
  tax_rate: 21, unit: 'unidad', is_available: true,
  image_url: '', cost_price: '',
};

const ShopProductsPage = () => {
  const { api } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const csvInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [scanMode, setScanMode] = useState(false);
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const [printProduct, setPrintProduct] = useState(null);
  const [showBulkPrint, setShowBulkPrint] = useState(false);
  const [showBrowseCatalog, setShowBrowseCatalog] = useState(false);
  const barcodeBuffer = useRef('');
  const barcodeTimeout = useRef(null);

  // USB Barcode Scanner listener — works when no input is focused
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Skip if user is typing in an input (unless it's the barcode field in scan mode)
      const tag = document.activeElement?.tagName;
      const isBarcodeInput = document.activeElement?.dataset?.testid === 'product-barcode-input';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
        if (!isBarcodeInput) return;
      }

      if (e.key === 'Enter' && barcodeBuffer.current.length > 3) {
        const scanned = barcodeBuffer.current;
        barcodeBuffer.current = '';
        handleBarcodeScanned(scanned);
        e.preventDefault();
        return;
      }
      if (e.key.length === 1) {
        barcodeBuffer.current += e.key;
        clearTimeout(barcodeTimeout.current);
        barcodeTimeout.current = setTimeout(() => { barcodeBuffer.current = ''; }, 100);
      }
    };
    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, [products, showModal]);

  const handleBarcodeScanned = useCallback((barcode) => {
    // If the product modal is open, fill the barcode field
    if (showModal) {
      setForm(f => ({ ...f, barcode }));
      toast.success(`Barcode scanned: ${barcode}`);
      return;
    }

    // Check if barcode exists in products
    const existing = products.find(p => p.barcode === barcode);
    if (existing) {
      // Open edit for existing product
      openEdit(existing);
      toast.info(`Found: ${existing.name}`);
    } else {
      // Open create with barcode pre-filled
      setEditingProduct(null);
      setForm({ ...emptyProduct, barcode });
      setShowModal(true);
      toast.info(`New barcode: ${barcode} — Add product details`);
    }
  }, [products, showModal]);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const shopsRes = await api.get('/shops');
      if (shopsRes.data.length === 0) { navigate('/shop/setup'); return; }
      const s = shopsRes.data[0];
      setShop(s);
      const prodsRes = await api.get(`/shops/${s.id}/products`);
      setProducts(prodsRes.data);
    } catch (e) { console.error('Failed to load products:', e); }
    finally { setLoading(false); }
  };

  const categories = useMemo(() =>
    [...new Set(products.map(p => p.category))].sort(), [products]);

  const filtered = useMemo(() => products.filter(p => {
    if (categoryFilter !== 'all' && p.category !== categoryFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return p.name.toLowerCase().includes(q) || p.barcode?.toLowerCase().includes(q);
    }
    return true;
  }), [products, categoryFilter, searchQuery]);

  const openCreate = () => { setEditingProduct(null); setForm(emptyProduct); setShowModal(true); };
  const openEdit = (p) => {
    setEditingProduct(p);
    setForm({
      name: p.name, description: p.description || '', price: p.price,
      category: p.category, barcode: p.barcode || '',
      stock_count: p.stock_count ?? '', low_stock_threshold: p.low_stock_threshold ?? 5,
      tax_rate: p.tax_rate, unit: p.unit, is_available: p.is_available,
      image_url: p.image_url || '', cost_price: p.cost_price ?? '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.price || !form.category) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        stock_count: form.stock_count === '' ? null : parseInt(form.stock_count),
        low_stock_threshold: parseInt(form.low_stock_threshold) || 5,
        cost_price: form.cost_price === '' ? null : parseFloat(form.cost_price),
        image_url: (form.image_url && !form.image_url.startsWith('data:')) ? form.image_url : (editingProduct?.image_url || null),
      };
      delete payload._pendingFile;

      let savedProduct;
      if (editingProduct) {
        const res = await api.put(`/shops/${shop.id}/products/${editingProduct.id}`, payload);
        savedProduct = res.data;
      } else {
        const res = await api.post(`/shops/${shop.id}/products`, payload);
        savedProduct = res.data;
      }

      // Upload pending image file for new products
      if (form._pendingFile && savedProduct?.id) {
        try {
          const formData = new FormData();
          formData.append('file', form._pendingFile);
          await api.post(`/shops/${shop.id}/products/${savedProduct.id}/upload-image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (err) { console.error('Image upload failed:', err); }
      }

      setShowModal(false);
      fetchData();
    } catch (e) { console.error('Save failed:', e); toast.error(e.response?.data?.detail || 'Error saving product'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (productId) => {
    try {
      await api.delete(`/shops/${shop.id}/products/${productId}`);
      setDeleteConfirm(null);
      fetchData();
    } catch (e) { console.error('Delete failed:', e); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(p => p.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected products?`)) return;
    try {
      await api.post(`/shops/${shop.id}/products/bulk-delete`, { product_ids: Array.from(selectedIds) });
      toast.success(`Deleted ${selectedIds.size} products`);
      setSelectedIds(new Set());
      fetchData();
    } catch (e) { toast.error('Bulk delete failed'); }
  };

  const handleBulkPrintBarcodes = () => {
    setShowBulkPrint(true);
  };

  const handleCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/shops/${shop.id}/products/import-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Imported ${res.data.imported} products`);
      if (res.data.errors?.length > 0) {
        toast.error(`${res.data.errors.length} rows had errors`);
      }
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); if (csvInputRef.current) csvInputRef.current.value = ''; }
  };

  const downloadCsvTemplate = () => {
    const header = 'name,price,category,barcode,tax_rate,stock_count,cost_price,image_url,description,unit\n';
    const example = 'Product Name,9.99,Category,8400000000001,21,,,,Product description,unidad\n';
    const blob = new Blob([header + example], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'product_import_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }

    // If we're editing an existing product, upload directly
    if (editingProduct) {
      setUploadingImage(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post(`/shops/${shop.id}/products/${editingProduct.id}/upload-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setForm(f => ({ ...f, image_url: res.data.image_url }));
        toast.success('Image uploaded');
      } catch (err) { toast.error('Upload failed'); }
      finally { setUploadingImage(false); }
    } else {
      // For new products — read as data URL for preview, save file for later
      // We'll upload after creating the product
      const reader = new FileReader();
      reader.onload = (ev) => {
        setForm(f => ({ ...f, image_url: ev.target.result, _pendingFile: file }));
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <ShopLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
        </div>
      </ShopLayout>
    );
  }

  return (
    <ShopLayout>
      <div className="space-y-6" data-testid="products-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-[#1e3a5f]">{t('products_title')}</h2>
            <p className="text-slate-500 text-sm">{t('products_total')}: {products.length}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input type="file" ref={csvInputRef} accept=".csv,.txt" onChange={handleCsvImport} className="hidden" data-testid="csv-input" />
            <button onClick={downloadCsvTemplate}
              className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm text-slate-600"
              data-testid="download-template-btn"
            >
              <Download className="w-4 h-4" /> Template
            </button>
            <button onClick={() => csvInputRef.current?.click()} disabled={importing}
              className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm text-slate-600"
              data-testid="import-csv-btn"
            >
              <Upload className="w-4 h-4" /> {importing ? 'Importing...' : 'Import CSV'}
            </button>
            {selectedIds.size > 0 && (
              <>
                <button onClick={() => handleBulkPrintBarcodes()}
                  className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 text-sm text-blue-600"
                  data-testid="bulk-print-barcodes-btn"
                >
                  <Printer className="w-4 h-4" /> Print Barcodes ({selectedIds.size})
                </button>
                <button onClick={handleBulkDelete}
                  className="flex items-center gap-1.5 px-3 py-2 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 text-sm text-red-600"
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-4 h-4" /> Delete ({selectedIds.size})
                </button>
              </>
            )}
            <button onClick={() => setShowBrowseCatalog(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-[#1e3a5f] text-[#1e3a5f] text-sm font-medium hover:bg-[#1e3a5f]/5 transition-colors"
              data-testid="browse-catalog-btn"
            >
              <Package className="w-5 h-5" /> Browse Catalog
            </button>
            <button onClick={openCreate}
              className="vf-btn-primary flex items-center gap-2"
              data-testid="add-product-btn"
            >
              <Plus className="w-5 h-5" /> {t('products_add')}
            </button>
          </div>
        </div>

        {/* Barcode Scanner Info */}
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5" data-testid="scanner-info">
          <ScanBarcode className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <p className="text-sm text-blue-800 flex-1">
            <span className="font-medium">Barcode Scanner Ready</span> — Scan a barcode anytime. Known products will open for editing. Unknown barcodes will create a new product.
          </p>
          <button
            onClick={() => setShowCameraScanner(!showCameraScanner)}
            className={`px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 transition-colors ${
              showCameraScanner ? 'bg-[#1e3a5f] text-white' : 'bg-white border border-blue-300 text-blue-700 hover:bg-blue-100'
            }`}
            data-testid="products-camera-scan-btn"
          >
            <ScanBarcode className="w-4 h-4" />
            Camera
          </button>
        </div>

        {/* Camera Scanner */}
        {showCameraScanner && (
          <div className="max-w-sm" data-testid="products-camera-scanner-wrapper">
            <CameraBarcodeScanner
              onScan={(barcode) => {
                setShowCameraScanner(false);
                handleBarcodeScanned(barcode);
              }}
              onClose={() => setShowCameraScanner(false)}
            />
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder={t('products_search')}
              className="w-full pl-10 pr-4 py-2.5 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
              data-testid="products-search"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button onClick={() => setCategoryFilter('all')}
              className={`px-4 py-2 rounded-sm whitespace-nowrap text-sm ${categoryFilter === 'all' ? 'bg-[#1e3a5f] text-white' : 'bg-white border border-slate-200 text-slate-600'}`}
            >{t('products_all')}</button>
            {categories.map(c => (
              <button key={c} onClick={() => setCategoryFilter(c)}
                className={`px-4 py-2 rounded-sm whitespace-nowrap text-sm ${categoryFilter === c ? 'bg-[#1e3a5f] text-white' : 'bg-white border border-slate-200 text-slate-600'}`}
              >{c}</button>
            ))}
          </div>
        </div>

        {/* Product Table */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 bg-white border-2 border-slate-100">
            <Package className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500 text-lg">{t('products_no_products')}</p>
            <p className="text-slate-400 text-sm mb-6">{t('products_add_first')}</p>
            <button onClick={openCreate} className="vf-btn-primary inline-flex items-center gap-2">
              <Plus className="w-5 h-5" /> {t('products_add')}
            </button>
          </div>
        ) : (
          <div className="bg-white border-2 border-slate-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="products-table">
                <thead>
                  <tr className="border-b-2 border-slate-100 text-left">
                    <th className="px-3 py-3 w-10">
                      <button onClick={toggleSelectAll} className="p-0.5" data-testid="select-all-btn">
                        {selectedIds.size === filtered.length && filtered.length > 0
                          ? <CheckSquare className="w-4 h-4 text-[#1e3a5f]" />
                          : <Square className="w-4 h-4 text-slate-400" />}
                      </button>
                    </th>
                    <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('products_name')}</th>
                    <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('products_category')}</th>
                    <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('products_price')}</th>
                    <th className="px-4 py-3 font-semibold text-[#1e3a5f]">Cost</th>
                    <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('products_stock')}</th>
                    <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('products_tax_rate')}</th>
                    <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('products_available')}</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(p => (
                    <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors"
                      data-testid={`product-row-${p.id}`}
                    >
                      <td className="px-3 py-3">
                        <button onClick={() => toggleSelect(p.id)} className="p-0.5">
                          {selectedIds.has(p.id)
                            ? <CheckSquare className="w-4 h-4 text-[#1e3a5f]" />
                            : <Square className="w-4 h-4 text-slate-300" />}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {p.image_url && (
                            <img src={p.image_url.startsWith('/api') ? `${API}${p.image_url}` : p.image_url}
                              alt="" className="w-8 h-8 rounded object-contain bg-slate-50 flex-shrink-0" />
                          )}
                          <div>
                            <p className="font-medium text-[#1e3a5f]">{p.name}</p>
                            {p.barcode && <p className="text-xs text-slate-400">{p.barcode}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{p.category}</td>
                      <td className="px-4 py-3 font-semibold text-[#22c55e]">€{p.price.toFixed(2)}</td>
                      <td className="px-4 py-3 text-slate-500">{p.cost_price ? `€${p.cost_price.toFixed(2)}` : '-'}</td>
                      <td className="px-4 py-3">
                        {p.stock_count === null ? (
                          <span className="text-slate-400">∞</span>
                        ) : (
                          <span className={p.stock_count <= (p.low_stock_threshold || 5) ? 'text-red-600 font-semibold' : 'text-slate-600'}>
                            {p.stock_count}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{p.tax_rate}%</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block w-3 h-3 rounded-full ${p.is_available ? 'bg-green-500' : 'bg-red-400'}`} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => setPrintProduct(p)}
                            className="p-2 hover:bg-blue-50 rounded-sm text-slate-500 hover:text-blue-600"
                            title="Print barcode"
                            data-testid={`print-barcode-${p.id}`}
                          ><Printer className="w-4 h-4" /></button>
                          <button onClick={() => openEdit(p)}
                            className="p-2 hover:bg-slate-100 rounded-sm text-slate-500 hover:text-[#1e3a5f]"
                            data-testid={`edit-product-${p.id}`}
                          ><Edit2 className="w-4 h-4" /></button>
                          <button onClick={() => setDeleteConfirm(p.id)}
                            className="p-2 hover:bg-red-50 rounded-sm text-slate-500 hover:text-red-600"
                            data-testid={`delete-product-${p.id}`}
                          ><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowModal(false)}>
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="w-full md:w-[560px] bg-white rounded-sm flex flex-col max-h-[85vh]"
                data-testid="product-modal"
                onClick={e => e.stopPropagation()}
              >
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-xl font-bold text-[#1e3a5f]">
                  {editingProduct ? t('products_edit') : t('products_create')}
                </h3>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-sm">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('products_name')} *</label>
                  <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                    data-testid="product-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('products_price')} *</label>
                    <input type="number" step="0.01" value={form.price}
                      onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                      className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                      data-testid="product-price-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('products_category')} *</label>
                    <input type="text" value={form.category}
                      onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                      className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                      data-testid="product-category-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('products_description')}</label>
                  <textarea value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    rows={2}
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none resize-none"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('products_barcode')}</label>
                    <div className="flex gap-1">
                      <input type="text" value={form.barcode}
                        onChange={e => setForm(f => ({ ...f, barcode: e.target.value }))}
                        placeholder="Scan or type barcode"
                        className="flex-1 px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                        data-testid="product-barcode-input"
                      />
                      <button type="button"
                        onClick={() => { setScanMode(true); toast.info('Scan a barcode now...'); }}
                        className={`px-3 py-2 rounded-sm border-2 transition-colors ${
                          scanMode ? 'bg-[#f97316] border-[#f97316] text-white' : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                        }`}
                        title="Activate barcode scanner"
                        data-testid="scan-barcode-btn"
                      >
                        <ScanBarcode className="w-5 h-5" />
                      </button>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">Use USB scanner or type manually</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('products_unit')}</label>
                    <select value={form.unit}
                      onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
                      className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                    >
                      {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('products_stock')}</label>
                    <input type="number" value={form.stock_count}
                      onChange={e => setForm(f => ({ ...f, stock_count: e.target.value }))}
                      placeholder="∞"
                      className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('products_low_threshold')}</label>
                    <input type="number" value={form.low_stock_threshold}
                      onChange={e => setForm(f => ({ ...f, low_stock_threshold: e.target.value }))}
                      className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('products_tax_rate')}</label>
                    <select value={form.tax_rate}
                      onChange={e => setForm(f => ({ ...f, tax_rate: parseFloat(e.target.value) }))}
                      className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                    >
                      {TAX_RATES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="available" checked={form.is_available}
                    onChange={e => setForm(f => ({ ...f, is_available: e.target.checked }))}
                    className="w-4 h-4 accent-[#22c55e]"
                  />
                  <label htmlFor="available" className="text-sm text-[#1e3a5f]">{t('products_available')}</label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e3a5f] mb-1">Cost Price</label>
                  <input type="number" step="0.01" value={form.cost_price}
                    onChange={e => setForm(f => ({ ...f, cost_price: e.target.value }))}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                    data-testid="product-cost-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e3a5f] mb-1">Product Image</label>
                  <div className="flex gap-2 items-start">
                    <div className="flex-1 space-y-2">
                      {/* File Upload */}
                      <label className="flex items-center justify-center gap-2 px-3 py-3 border-2 border-dashed border-slate-300 rounded-lg hover:border-[#f97316] hover:bg-[#f97316]/5 cursor-pointer transition-colors"
                        data-testid="image-upload-label"
                      >
                        <Upload className="w-5 h-5 text-slate-400" />
                        <span className="text-sm text-slate-500">{uploadingImage ? 'Uploading...' : 'Upload Image'}</span>
                        <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploadingImage}
                          data-testid="image-upload-input"
                        />
                      </label>
                      {/* Or URL */}
                      <input type="text" value={form.image_url}
                        onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                        placeholder="Or paste image URL..."
                        className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none text-sm"
                        data-testid="product-image-input"
                      />
                    </div>
                    {form.image_url && (
                      <img
                        src={form.image_url.startsWith('/api') ? `${API}${form.image_url}` : form.image_url}
                        alt="" className="w-16 h-16 rounded-lg object-contain bg-slate-50 border border-slate-200 flex-shrink-0"
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="p-4 border-t border-slate-200 flex gap-3">
                <button onClick={() => setShowModal(false)} className="vf-btn-secondary flex-1">{t('products_cancel')}</button>
                <button onClick={handleSave} disabled={saving || !form.name || !form.price || !form.category}
                  className="vf-btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="save-product-btn"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                  {t('products_save')}
                </button>
              </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Delete Confirmation */}
      <AnimatePresence>
        {deleteConfirm && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setDeleteConfirm(null)}>
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white p-6 rounded-sm w-[360px]"
                data-testid="delete-confirm-modal"
              onClick={e => e.stopPropagation()}
            >
              <h4 className="text-lg font-bold text-[#1e3a5f] mb-2">{t('products_confirm_delete')}</h4>
              <div className="flex gap-3 mt-4">
                <button onClick={() => setDeleteConfirm(null)} className="vf-btn-secondary flex-1">{t('products_cancel')}</button>
                <button onClick={() => handleDelete(deleteConfirm)}
                  className="flex-1 bg-red-600 text-white px-4 py-2 font-medium hover:bg-red-700 transition-colors"
                  data-testid="confirm-delete-btn"
                >{t('products_yes_delete')}</button>
              </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Barcode Print Modal */}
      <AnimatePresence>
        {printProduct && (
          <BarcodePrintModal
            product={printProduct}
            onClose={() => setPrintProduct(null)}
            onBarcodeUpdate={async (productId, newBarcode) => {
              try {
                await api.put(`/shops/${shop.id}/products/${productId}`, { barcode: newBarcode });
                toast.success('Barcode saved to product');
                fetchData();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Failed to save barcode');
              }
            }}
          />
        )}
      </AnimatePresence>

      {/* Bulk Barcode Print Modal */}
      <AnimatePresence>
        {showBulkPrint && (
          <BulkBarcodePrintModal
            products={products.filter(p => selectedIds.has(p.id))}
            onClose={() => setShowBulkPrint(false)}
          />
        )}
      </AnimatePresence>

      {/* Browse Global Catalog */}
      <AnimatePresence>
        {showBrowseCatalog && shop && (
          <BrowseCatalogModal
            shopId={shop.id}
            onClose={() => setShowBrowseCatalog(false)}
            onAdded={fetchData}
          />
        )}
      </AnimatePresence>
    </ShopLayout>
  );
};

export default ShopProductsPage;
